import { api, downloadFile } from "@/lib/api";

export interface OverviewKpis {
  total_tickets: number;
  open_tickets: number;
  overdue_tickets: number;
  resolved_in_period: number;
  sla_compliance_pct: number;
  avg_resolution_hours: number;
  active_customers: number;
  low_stock_products: number;
  expiring_amc: number;
}

export interface Overview {
  period: { start: string; end: string };
  kpis: OverviewKpis;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_site: Record<string, number>;
  created_per_day: { date: string; count: number; resolved?: number }[];
}

/** Optional slicers shared by every report and the overview. */
export interface ReportFilters {
  start?: string;
  end?: string;
  site?: string;
  customer_id?: string;
  engineer_id?: string;
  status?: string;
  priority?: string;
  category_id?: string;
  call_type?: string;
}

export interface ReportTable {
  title: string;
  columns: string[];
  rows: (string | number | null)[][];
}

export interface BillingRate {
  id: string;
  customer_id: string | null;
  label: string;
  hourly_rate: string;
  created_at: string;
}

export type ReportName =
  | "tickets"
  | "calls"
  | "sla"
  | "engineers"
  | "site-admins"
  | "customers"
  | "sites"
  | "amc"
  | "revenue"
  | "call-revenue";
export type ExportFormat = "csv" | "xlsx" | "pdf";

/** Build a query string from the shared filters, dropping empty values. */
function filterParams(f: ReportFilters = {}): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v) p.set(k, v);
  }
  return p;
}

function suffix(f: ReportFilters): string {
  const s = filterParams(f).toString();
  return s ? `?${s}` : "";
}

export function getOverview(f: ReportFilters = {}): Promise<Overview> {
  return api.get<Overview>(`/reports/overview${suffix(f)}`);
}

export function getReport(name: ReportName, f: ReportFilters = {}): Promise<ReportTable> {
  return api.get<ReportTable>(`/reports/${name}${suffix(f)}`);
}

export function exportReport(
  name: ReportName,
  format: ExportFormat,
  f: ReportFilters = {},
  cols?: number[],
): Promise<void> {
  const p = filterParams(f);
  p.set("format", format);
  if (cols && cols.length) p.set("cols", cols.join(","));
  return downloadFile(`/reports/${name}/export?${p.toString()}`, `${name}-report.${format}`);
}

// --- billing rates ---

export const listBillingRates = () => api.get<BillingRate[]>("/billing-rates");
export const createBillingRate = (body: {
  customer_id?: string | null;
  label: string;
  hourly_rate: string;
}) => api.post<BillingRate>("/billing-rates", body);
export const updateBillingRate = (id: string, body: { label?: string; hourly_rate?: string }) =>
  api.patch<BillingRate>(`/billing-rates/${id}`, body);
export const deleteBillingRate = (id: string) =>
  api.delete<{ message: string }>(`/billing-rates/${id}`);
